import { NgModule } from '@angular/core';
import { CanActivateFn, Routes, RouterModule } from '@angular/router';

import { AdministrativeAccountsListComponent } from './modules/admin/pages/accounts-list/administrative-accounts-list.component';
import { AdministrativeAccountsUsersReportComponent } from './modules/admin/pages/accounts-users-report/accounts-users-report.component';
import { CasesAdminComponent } from './modules/admin/pages/cases-admin/cases-admin.component';
import { RegisterCaseComponent } from './modules/admin/pages/cases-admin/register-case/register-case.component';
import { CommunicareScheduleWebhook } from './modules/admin/pages/communicare-schedule-webhook/communicare-schedule-webhook.component';
import { AdministrativeConsolidatedSchedulesReportComponent } from './modules/admin/pages/consolidated-schedules-report/consolidated-schedules-report.component';
import { DoctorsComponent } from './modules/admin/pages/doctors/doctors.component';
import { EditIndicationComponent } from './modules/admin/pages/edit-indication/edit-indication.component';
import { AdministrativeIndicationsFinancialComponent } from './modules/admin/pages/indication-financial/indication-financial.component';
import { AdministrativeIndicationsComponent } from './modules/admin/pages/indications-report/indications-report.component';
import { InvoiceDoctorComponent } from './modules/admin/pages/invoice-doctor/invoice-doctor.component';
import { ProcedurePlansComponent } from './modules/admin/pages/procedure-plans/procedure-plans.component';
import { SpecialtiesComponent } from './modules/admin/pages/specialties/specialties.component';
import { TakeAccountComponent } from './modules/admin/pages/take-account/take-account.component';
import { IndicationAnalyticsComponent } from './pages/administrative/indication-analytics/indication-analytics.component';
import { BetaPaymentComponent } from './pages/beta-payment/beta-payment.component';
import { CasesComponent } from './pages/cases/cases.component';
import { CompleteRegistrationComponent } from './pages/complete-registration/complete-registration.component';
import { ContractorComponent } from './pages/contractor/contractor.component';
import { ForgotPasswordComponent } from './pages/forgot-password/forgot-password.component';
import { InvitationComponent } from './pages/invitation/invitation.component';
import { InvoiceListComponent } from './pages/invoice/invoice-list.component';
import { LoginComponent } from './pages/login/login.component';
import { PatientsComponent } from './pages/patients/patients.component';
import { PlanPricesComponent } from './pages/plan-prices/plan-prices.component';
import { PlansComponent } from './pages/plans/plans.component';
import { UserRegisterComponent } from './pages/public/user-register/user-register.component';
import { AccountRegisterSuccessComponent } from './pages/public/views/account-register-success/account-register-success.component';
import { AccountRegisterComponent } from './pages/public/views/account-register/account-register.component';
import { AccountsCsvReportComponent } from './pages/reports/accounts-csv-report/accounts-csv-report.component';
import { DelayedAttendanceReportComponent } from './pages/reports/delayed-attendance-report/delayed-attendance-report.component';
import { IndicationsByDoctorComponent } from './pages/reports/nps/indications-by-doctor/indications-by-doctor.component';
import { NpsAverageComponent } from './pages/reports/nps/nps-average/nps-average.component';
import { SignatureComponent } from './pages/signature/signature.component';
import { UsersComponent } from './pages/users/users.component';
import { WalletComponent } from './pages/wallet/wallet.component';
import { AdminGuard } from './shared/guards/admin.guard';
import { FinanceiroRoleGuard } from './shared/guards/financeiro-role.guard';
import { LoggedInGuard } from './shared/guards/loggedin.guard';
import { SuperAdminGuard } from './shared/guards/superAdmin.guard';

const redirectLegacyGuard: CanActivateFn = () => {
  // Direcionamos para o front novo
  window.location.href = '/login';
  return false;
};

//TODO: Impl. Lazy loading modules
const routes: Routes = [
  { path: '', pathMatch: 'full', canActivate: [redirectLegacyGuard], children: [] },
  { path: 'login', component: LoginComponent },
  { path: 'login/:token', component: LoginComponent },
  { path: 'invitation/:token', component: InvitationComponent },
  { path: 'beta/:valid', component: BetaPaymentComponent },
  {
    path: 'complete-registration/:token',
    component: CompleteRegistrationComponent,
  },
  { path: 'profile/:token', component: CompleteRegistrationComponent },
  // { path: 'register', component: RegisterComponent },
  // { path: 'register/plus-plan', component: RegisterComponent },
  { path: 'forgot-password', component: ForgotPasswordComponent },
  { path: 'reset-password/:token', component: ForgotPasswordComponent },
  {
    path: 'wallet',
    component: WalletComponent,
    canLoad: [LoggedInGuard],
    canActivate: [LoggedInGuard],
  },
  {
    path: 'patients',
    component: PatientsComponent,
    canLoad: [LoggedInGuard],
    canActivate: [LoggedInGuard],
  },
  {
    path: 'signature',
    component: SignatureComponent,
    canActivate: [AdminGuard],
  },
  {
    path: 'contractor',
    component: ContractorComponent,
    canActivate: [AdminGuard],
  },
  { path: 'users', component: UsersComponent, canActivate: [AdminGuard] },
  {
    path: 'take-account',
    component: TakeAccountComponent,
    canActivate: [SuperAdminGuard],
  },
  {
    path: 'edit-indication',
    component: EditIndicationComponent,
    canActivate: [SuperAdminGuard],
  },
  {
    path: 'specialties',
    component: SpecialtiesComponent,
    canActivate: [SuperAdminGuard],
  },
  {
    path: 'doctors',
    component: DoctorsComponent,
    canActivate: [SuperAdminGuard],
  },
  {
    path: 'reports/nps/indications-by-doctor',
    component: IndicationsByDoctorComponent,
    canActivate: [AdminGuard],
  },
  {
    path: 'reports/nps/average',
    component: NpsAverageComponent,
    canActivate: [AdminGuard],
  },
  // {
  //   path: 'reports/users',
  //   component: SubscriptionReportComponent,
  //   canActivate: [AdminGuard],
  // },
  {
    path: 'reports/delayed-attendance',
    component: DelayedAttendanceReportComponent,
    canActivate: [AdminGuard],
  },
  {
    path: 'plans',
    component: PlansComponent,
    canActivate: [SuperAdminGuard],
  },
  {
    path: 'plans/:id/prices',
    component: PlanPricesComponent,
    canActivate: [SuperAdminGuard],
  },
  {
    path: 'procedure',
    component: ProcedurePlansComponent,
    canActivate: [SuperAdminGuard],
  },
  {
    path: 'cases-admin',
    component: CasesAdminComponent,
    canActivate: [SuperAdminGuard],
  },
  {
    path: 'cases-admin/novo-caso',
    component: RegisterCaseComponent,
    canActivate: [SuperAdminGuard],
  },
  {
    path: 'cases-admin/:id',
    component: RegisterCaseComponent,
    canActivate: [SuperAdminGuard],
  },
  {
    path: 'cases',
    component: CasesComponent,
    canActivate: [LoggedInGuard],
  },
  {
    path: 'reports/invoices',
    component: InvoiceListComponent,
    canActivate: [SuperAdminGuard],
  },
  {
    path: 'public/register',
    component: UserRegisterComponent,
  },
  {
    path: 'public/account/register',
    component: AccountRegisterComponent,
  },
  {
    path: 'public/account/register/success',
    component: AccountRegisterSuccessComponent,
  },
  {
    path: 'reports/accounts-csv',
    component: AccountsCsvReportComponent,
    canActivate: [SuperAdminGuard],
  },
  {
    path: 'administrative/indications-analytics',
    component: IndicationAnalyticsComponent,
    canActivate: [AdminGuard],
  },
  {
    path: 'administrative/accounts',
    component: AdministrativeAccountsListComponent,
    canActivate: [SuperAdminGuard],
  },
  {
    path: 'administrative/indications',
    component: AdministrativeIndicationsComponent,
    canActivate: [SuperAdminGuard],
  },
  {
    path: 'administrative/indication-financial',
    component: AdministrativeIndicationsFinancialComponent,
    canActivate: [FinanceiroRoleGuard],
  },
  {
    path: 'reports/doctor-payments',
    component: AdministrativeConsolidatedSchedulesReportComponent,
    canActivate: [FinanceiroRoleGuard],
  },
  {
    path: 'reports/invoice-doctor',
    component: InvoiceDoctorComponent,
    canActivate: [FinanceiroRoleGuard],
  },
  {
    path: 'reports/accounts-users',
    component: AdministrativeAccountsUsersReportComponent,
    canActivate: [SuperAdminGuard],
  },
  {
    path: 'webhook/communicare-agendamento',
    component: CommunicareScheduleWebhook,
    canActivate: [SuperAdminGuard],
  },
  {
    path: 'v2',
    loadChildren: () => import('./_pages/private/private.module').then((m) => m.PrivateModule),
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { useHash: true })],
  exports: [RouterModule],
})
export class AppRoutingModule {}
